import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../../store/useStore';
import { FaCheckCircle, FaShoppingCart, FaLock } from 'react-icons/fa';
import toast from 'react-hot-toast';

import { INITIAL_FORM } from './checkoutConstants';
import { openCashfreeCheckout, shouldVerifyCashfreePayment } from '../../utils/cashfree';
import StepIndicator from './StepIndicator';
import OrderSummary from './OrderSummary';
import OrderConfirmed from './OrderConfirmed';
import AddressStep from './steps/AddressStep';
import ReviewStep from './steps/ReviewStep';
import PaymentStep from './steps/PaymentStep';

const isPaidStatus = (status) =>
  ['PAID', 'SUCCESS'].includes(String(status || '').toUpperCase());

// Detect out-of-stock API errors
const isOutOfStockError = (msg = '') =>
  /out of stock/i.test(msg) || /available:\s*0/i.test(msg);

const CheckoutPage = () => {
  const navigate = useNavigate();
  const {
    cart,
    clearCart,
    fetchCart,
    cart_meta,
    user_addresses,
    fetchUserAddresses,
    placeOrder,
    place_order_loading,
    getOrderDetails,
    verifyPayment,
    setPaymentSession,
    addNotification,
    isAuthenticated,
    openAuthModal,
    buyNowItem,
    clearBuyNowItem,
    is_guest_cart,
    mergeGuestCartOnLogin,
  } = useStore();

  const activeItems = buyNowItem ? [buyNowItem] : cart;

  // Use server totals from cart_meta; for buyNow compute locally
  const { subtotal, delivery, gstTotal, total } = (() => {
    if (buyNowItem) {
      const itemSubtotal = parseFloat(buyNowItem.price || 0) * (buyNowItem.quantity || 1);
      const itemGst = (itemSubtotal * parseFloat(buyNowItem.gst || 0)) / 100;
      return { subtotal: itemSubtotal, gstTotal: itemGst, delivery: 0, total: itemSubtotal + itemGst };
    }
    return {
      subtotal:  parseFloat(cart_meta?.subtotal   ?? 0),
      gstTotal:  parseFloat(cart_meta?.tax_amount  ?? 0),
      delivery:  parseFloat(cart_meta?.delivery_fee ?? 0),
      total:     parseFloat(cart_meta?.total_amount ?? 0),
    };
  })();

  const [step, setStep] = useState(2);
  const [paymentMethod, setPaymentMethod] = useState('COD');
  const [confirmedOrder, setConfirmedOrder] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [orderError, setOrderError] = useState(null);

  // Fetch addresses on mount and auto-select the default one
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchUserAddresses();
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Once addresses load, auto-select default (or first) if none selected yet
  useEffect(() => {
    if (selectedAddressId || user_addresses.length === 0) return;
    const def = user_addresses.find((a) => a.is_default) || user_addresses[0];
    if (def) setSelectedAddressId(def.address_id ?? def.id);
  }, [user_addresses]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-sm text-center">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 shadow-xl">
            <FaLock className="h-9 w-9 text-white" aria-hidden="true" />
          </div>
          <h2 className="mb-3 text-2xl font-bold text-gray-900">Login Required</h2>
          <p className="mb-8 text-gray-500">Please login to proceed with your checkout.</p>
          <button
            onClick={openAuthModal}
            className="w-full rounded-2xl bg-gradient-to-r from-primary-600 to-primary-700 px-8 py-4 font-bold text-white shadow-lg shadow-primary-600/30 transition-all hover:from-primary-700 hover:to-primary-800"
          >
            Login / Sign Up
          </button>
          <button
            onClick={() => navigate('/cart')}
            className="mt-4 w-full rounded-2xl border-2 border-gray-200 bg-white px-8 py-3.5 font-semibold text-gray-600 transition-all hover:border-primary-300 hover:text-primary-600"
          >
            Back to Cart
          </button>
        </div>
      </div>
    );
  }

  const resolveAddressId = () => {
    if (selectedAddressId) return Number(selectedAddressId);
    const def = user_addresses.find((a) => a.is_default) || user_addresses[0];
    if (def) return Number(def.address_id ?? def.id);
    return null;
  };

  const completeSuccessfulOrder = async (order) => {
    if (buyNowItem) {
      clearBuyNowItem();
      useStore.getState().fetchCart?.();
    } else {
      await clearCart();
    }

    addNotification({
      type: 'success',
      title: 'Order placed successfully',
      message: `Your order ${order.order_number || ''} has been placed and is being prepared.`,
      created_at: new Date().toISOString(),
      order_id: order.order_id,
    });

    toast.custom(
      (t) => (
        <div
          className={`flex w-[min(92vw,420px)] items-start gap-3 rounded-2xl border border-emerald-200 bg-white px-4 py-3 shadow-2xl shadow-emerald-900/10 transition-all duration-300 ${
            t.visible ? 'translate-y-0 scale-100 opacity-100' : '-translate-y-2 scale-95 opacity-0'
          }`}
        >
          <span className="relative mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
            <span className="absolute h-full w-full animate-ping rounded-full bg-emerald-200 opacity-60" />
            <FaCheckCircle className="relative h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-extrabold text-gray-900">Order placed successfully</p>
            <p className="mt-0.5 text-xs leading-5 text-gray-500">
              {order.order_number || 'Your order'} is confirmed. Track it from My Orders.
            </p>
          </div>
        </div>
      ),
      { duration: 4500 },
    );

    setConfirmedOrder({ ...order, form, subtotal, delivery, gstTotal, total });
  };

  const verifyCashfreeOrder = async (order) => {
    try {
      return await verifyPayment(order.order_id);
    } catch {
      const fallbackOrder = await getOrderDetails(order.order_id);
      return {
        order: fallbackOrder,
        payment_status: fallbackOrder?.payment_status,
      };
    }
  };

  const openCashfree = async (sessionId, order) => {
    const paymentResult = await openCashfreeCheckout({
      paymentSessionId: sessionId,
      redirectTarget: '_modal',
      returnUrl: `${window.location.origin}/payment/return?order_id=${order.order_id}`,
    });

    if (paymentResult?.error) {
      throw new Error(paymentResult.error.message || 'Payment was not completed.');
    }

    if (!shouldVerifyCashfreePayment(paymentResult)) {
      throw new Error('Payment was not completed. You can retry from My Orders.');
    }

    const verified = await verifyCashfreeOrder(order);
    if (isPaidStatus(verified?.payment_status)) {
      return verified.order || { ...order, payment_status: verified.payment_status };
    }

    throw new Error('Payment is pending or failed. Please check My Orders.');
  };

  const handlePlaceOrder = async (notes = '') => {
    setOrderError(null);
    const address_id = resolveAddressId();

    if (!address_id) {
      toast.error('Please select or add a delivery address.');
      setStep(1);
      return;
    }

    if (!buyNowItem && is_guest_cart) {
      await mergeGuestCartOnLogin();
    }

    try {
      const result = await placeOrder({
        address_id,
        payment_method: paymentMethod,
        notes,
        buyNowItem: buyNowItem ?? null,
      });

      if (paymentMethod !== 'COD' && result.payment_session_id) {
        setPaymentSession(result.payment_session_id, result.cashfree_order_id);
        const verifiedOrder = await openCashfree(result.payment_session_id, result.order);
        await completeSuccessfulOrder(verifiedOrder);
        return;
      }

      await completeSuccessfulOrder(result.order);
    } catch (err) {
      const msg = err.message || 'Failed to place order. Please try again.';

      if (isOutOfStockError(msg)) {
        // Re-sync the server cart so stale/out-of-stock items are visible
        try { await fetchCart(); } catch { /* best-effort */ }

        toast.custom(
          (t) => (
            <div
              className={`flex w-[min(92vw,440px)] items-start gap-3 rounded-2xl border border-red-200 bg-white px-4 py-3 shadow-2xl transition-all duration-300 ${
                t.visible ? 'translate-y-0 scale-100 opacity-100' : '-translate-y-2 scale-95 opacity-0'
              }`}
            >
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-500 text-lg">
                ⚠️
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-extrabold text-gray-900">Item out of stock</p>
                <p className="mt-0.5 text-xs leading-5 text-gray-500">{msg}</p>
                <button
                  onClick={() => { toast.dismiss(t.id); navigate('/cart'); }}
                  className="mt-2 rounded-lg bg-red-50 px-3 py-1 text-xs font-bold text-red-600 hover:bg-red-100"
                >
                  Go to Cart → Remove item
                </button>
              </div>
            </div>
          ),
          { duration: 8000 },
        );

        // Navigate back to cart so user can remove the out-of-stock item
        navigate('/cart');
        return;
      }

      setOrderError(msg);
    }
  };

  if (confirmedOrder) {
    return <OrderConfirmed order={confirmedOrder} />;
  }

  if (!buyNowItem && activeItems.length === 0 && !confirmedOrder) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-primary-50 text-primary-500">
            <FaShoppingCart className="h-9 w-9" aria-hidden="true" />
          </div>
          <h2 className="mb-3 text-2xl font-bold text-gray-900">Your cart is empty</h2>
          <p className="mb-8 text-gray-500">Add some items before checking out.</p>
          <button
            onClick={() => navigate('/products')}
            className="rounded-2xl bg-primary-600 px-8 py-3.5 font-bold text-white shadow-lg shadow-primary-600/30 transition-all hover:bg-primary-700"
          >
            Browse Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="mb-10">
          <button
            onClick={() => navigate('/cart')}
            className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-500 transition-colors hover:text-primary-600"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Cart
          </button>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">Checkout</h1>
        </div>

        <StepIndicator currentStep={step} onStepClick={setStep} />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            {step === 1 && (
              <AddressStep
                form={form}
                onChange={handleChange}
                onNext={() => setStep(2)}
                onAddressSelect={(id) => setSelectedAddressId(id)}
              />
            )}
            {/* Back button when on address step so user can return to review */}
            {step === 2 && (
              <ReviewStep
                form={form}
                cart={activeItems}
                userAddresses={user_addresses}
                selectedAddressId={selectedAddressId}
                onEditAddress={() => setStep(1)}
                onNext={() => setStep(3)}
              />
            )}
            {step === 3 && (
              <PaymentStep
                total={total}
                paymentMethod={paymentMethod}
                onMethodChange={setPaymentMethod}
                onPlaceOrder={handlePlaceOrder}
                placing={place_order_loading}
                orderError={orderError}
              />
            )}
          </div>

          <div className="lg:col-span-1">
            <OrderSummary
              cart={activeItems}
              subtotal={subtotal}
              delivery={delivery}
              gstTotal={gstTotal}
              total={total}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
